import { promiseState } from '@passionware/platform-react';
import { rd } from '@passionware/monads';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Callout } from '__types__/v1/models/Callout';
import { Form } from 'v5/design-system/Form';
import { CalloutDetailsFields } from 'v5/features/callouts/_shared/CalloutDetailsFields';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { CalloutDuplicateFormData } from 'v5/features/callouts/callout-create.types';
import { normalizeCalloutSlugForSubmit } from 'v5/api/callouts/callout-slug.logic';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { WithServices } from 'v5/platform/ts/services';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

export interface DuplicateCalloutDialogContentProps
  extends WithServices<
    [
      WithResourceTypeService,
      WithCalloutsService,
      WithAppScopeService,
      WithEnterpriseService
    ]
  > {
  callout: Callout;
  onSubmit?: (data: CalloutDuplicateFormData) => Promise<void> | void;
  open?: boolean;
}

export function DuplicateCalloutDialogContent({
  callout,
  onSubmit,
  open = true,
  services
}: DuplicateCalloutDialogContentProps) {
  const { t } = useTranslation('callouts');

  const form = useForm<CalloutDuplicateFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: callout.title ?? '',
      slug: '',
      resource_type_id: callout.resource_type_id ?? 0
    }
  });

  const mutation = promiseState.useMutation(
    async (data: CalloutDuplicateFormData) => {
      const slug = await services.calloutsService.resolveUniqueSlug(
        normalizeCalloutSlugForSubmit(data.slug),
        { workspaceId: callout.enterprise_workspace_id ?? null }
      );
      return onSubmit?.({ ...data, slug });
    }
  );

  const title = form.watch('title');
  const { isValid } = form.formState;
  const canSubmit = Boolean(title?.trim()) && isValid;

  useRegisterDirtyState(form.formState.isDirty);

  const scopeRd = services.appScopeService.useAppScope();
  const resourceTypes = services.resourceTypeService.useResourceTypes(
    rd.tryMap(scopeRd, scope =>
      appScopeUtils.identityFromEntityWorkspace(
        scope.organizationId,
        callout.enterprise_workspace_id
      )
    )
  );

  return (
    <Form {...form}>
      <FormDialogContent
        onSubmit={form.handleSubmit(data => mutation.track(data))}
        open={open}
        title={t('CalloutDialog.duplicateCallout')}
        saveText={t('CalloutDialog.duplicate')}
        mutation={mutation.state}
        className="max-w-150"
        footerContent={<DialogErrorFooter mutation={mutation.state} />}
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
            visuallyDisabled={!canSubmit}
          >
            {t('CalloutDialog.duplicate')}
          </AsyncButton>
        }
      >
        <div className="space-y-4">
          <CalloutDetailsFields
            form={form}
            resourceTypes={resourceTypes}
            workspaceId={callout.enterprise_workspace_id ?? null}
            services={services}
          />
        </div>
      </FormDialogContent>
    </Form>
  );
}
